// Typography configuration shared between admin and frontend.
//
// IMPORTANT: every entry below MUST have a `google` URL fragment so the font
// can be auto-loaded on any visitor's browser, OR a `selfHosted: true` flag
// indicating the font is already self-hosted in /public/fonts/ via @font-face.
// If a font is `google: null` AND `selfHosted: false`, it will silently fall
// back to the browser's default sans-serif on machines without it installed.
//
// "Avenir Next" is now SELF-HOSTED via WOFF2 webfonts under
// /public/fonts/avenir-next/ (Regular 400, Medium 500, Demi Bold 600,
// Bold 700, Heavy 900). The @font-face declarations live at the top of
// App.css. This replaces the previous Mulish-substitute approach — Avenir
// Next now renders identically on every device and browser.
export const FONT_LIBRARY = [
  { label: 'Avenir Next', value: "'Avenir Next', 'Avenir', sans-serif", google: null, selfHosted: true },
  { label: 'Mulish (Avenir-like, web-safe)', value: "'Mulish', 'Avenir Next', 'Avenir', sans-serif", google: 'Mulish:wght@400;500;600;700;800;900' },
  { label: 'Bebas Neue', value: "'Bebas Neue', sans-serif", google: 'Bebas+Neue' },
  { label: 'Barlow Condensed', value: "'Barlow Condensed', sans-serif", google: 'Barlow+Condensed:wght@400;500;600;700;800' },
  { label: 'Inter', value: "'Inter', sans-serif", google: 'Inter:wght@400;500;600;700;800' },
  { label: 'Montserrat', value: "'Montserrat', sans-serif", google: 'Montserrat:wght@400;500;600;700;800' },
  { label: 'Playfair Display', value: "'Playfair Display', serif", google: 'Playfair+Display:wght@400;500;600;700;800' },
  { label: 'Raleway', value: "'Raleway', sans-serif", google: 'Raleway:wght@400;500;600;700;800' },
  { label: 'Outfit', value: "'Outfit', sans-serif", google: 'Outfit:wght@400;500;600;700;800' },
  { label: 'Plus Jakarta Sans', value: "'Plus Jakarta Sans', sans-serif", google: 'Plus+Jakarta+Sans:wght@400;500;600;700;800' },
  { label: 'DM Sans', value: "'DM Sans', sans-serif", google: 'DM+Sans:wght@400;500;600;700' },
  { label: 'Space Grotesk', value: "'Space Grotesk', sans-serif", google: 'Space+Grotesk:wght@400;500;600;700' },
  { label: 'Oswald', value: "'Oswald', sans-serif", google: 'Oswald:wght@400;500;600;700' },
  { label: 'Poppins', value: "'Poppins', sans-serif", google: 'Poppins:wght@400;500;600;700;800' },
  { label: 'Roboto', value: "'Roboto', sans-serif", google: 'Roboto:wght@400;500;700;900' },
  { label: 'Lora', value: "'Lora', serif", google: 'Lora:wght@400;500;600;700' },
  { label: 'Anton (Display)', value: "'Anton', sans-serif", google: 'Anton' },
  { label: 'Archivo Black (Display)', value: "'Archivo Black', sans-serif", google: 'Archivo+Black' },
];

// Helper: ensure every font in FONT_LIBRARY is loaded into the document.
// Idempotent — calling it multiple times only adds <link> tags once each.
// Used by both the admin (so previews render correctly) AND the public site
// (so any saved typography override actually renders for every visitor).
export function preloadAllFonts() {
  if (typeof document === 'undefined') return;
  FONT_LIBRARY.forEach((f) => {
    if (!f.google) return;
    const id = `cs-font-${f.google.replace(/[^a-zA-Z0-9]/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${f.google}&display=swap`;
    document.head.appendChild(link);
  });
}

export const FONT_WEIGHTS = [
  { label: 'Regular (400)', value: '400' },
  { label: 'Medium (500)', value: '500' },
  { label: 'Demi Bold (600)', value: '600' },
  { label: 'Bold (700)', value: '700' },
  { label: 'Extra Bold (800)', value: '800' },
];

export const TEXT_TRANSFORMS = [
  { label: 'None', value: 'none' },
  { label: 'Uppercase', value: 'uppercase' },
  { label: 'Lowercase', value: 'lowercase' },
  { label: 'Capitalize', value: 'capitalize' },
];

// Default typography style (used when no override is set)
export const DEFAULT_TYPO = {
  fontFamily: "'Avenir Next', 'Avenir', sans-serif",
  fontSize: '',
  fontWeight: '',
  textTransform: '',
  letterSpacing: '',
  lineHeight: '',
};

// Define which text elements exist per section
export const SECTION_TEXT_ELEMENTS = {
  hero: [
    { key: 'headline', label: 'Hero Headline' },
    { key: 'subheadline', label: 'Hero Subheadline' },
    { key: 'ctaButtons', label: 'CTA Buttons' },
  ],
  about: [
    { key: 'title', label: 'Section Title' },
    { key: 'body', label: 'Body Text' },
    { key: 'philosophyLines', label: 'Philosophy Lines' },
  ],
  capabilities: [
    { key: 'title', label: 'Section Title' },
    { key: 'subtitle', label: 'Subtitle' },
    { key: 'cardTitle', label: 'Card Titles' },
    { key: 'cardBullets', label: 'Card Bullet Text' },
    { key: 'statNumber', label: 'Impact Stat Numbers' },
    { key: 'statLabel', label: 'Impact Stat Labels' },
  ],
  gallery: [
    { key: 'title', label: 'Section Title' },
    { key: 'overlayTitle', label: 'Overlay Title' },
    { key: 'photoCount', label: 'Photo Count Text' },
  ],
  sustainability: [
    { key: 'title', label: 'Section Title' },
    { key: 'intro', label: 'Intro Text' },
    { key: 'metricValue', label: 'Metric Values' },
    { key: 'metricLabel', label: 'Metric Labels' },
    { key: 'platformTitle', label: 'Platform Card Titles' },
    { key: 'platformBody', label: 'Platform Card Body' },
  ],
  proprietary: [
    { key: 'title', label: 'Section Title' },
    { key: 'label', label: 'Section Label' },
    { key: 'cardTitle', label: 'Card Titles' },
  ],
  contact: [
    { key: 'headline', label: 'Headline' },
    { key: 'eyebrow', label: 'Eyebrow Text' },
    { key: 'subtitle', label: 'Subtitle' },
    { key: 'ctaButton', label: 'CTA Button' },
  ],
  footer: [
    { key: 'navLinks', label: 'Navigation Links' },
    { key: 'tagline', label: 'Tagline' },
  ],
  navigation: [
    { key: 'navLinks', label: 'Navigation Links' },
    { key: 'logo', label: 'Logo Text' },
  ],
};

// Build inline style object from typography settings
export function buildTypoStyle(typo) {
  if (!typo) return {};
  const style = {};
  if (typo.fontFamily) style.fontFamily = typo.fontFamily;
  if (typo.fontSize) style.fontSize = typo.fontSize;
  if (typo.fontWeight) style.fontWeight = typo.fontWeight;
  if (typo.textTransform) style.textTransform = typo.textTransform;
  if (typo.letterSpacing) style.letterSpacing = typo.letterSpacing;
  if (typo.lineHeight) style.lineHeight = typo.lineHeight;
  return style;
}

// Get typography for a specific section + element from content
export function getTypo(content, section, element) {
  const typo = content?.typography?.[section]?.[element];
  return typo ? buildTypoStyle(typo) : {};
}
